import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import noop from 'lodash/noop';

import {
  ChangePasswordRequest,
  CopiedProject,
  CopyProjectRequest,
  CreditsRequest,
  ImpersonateRequest,
  InvitationRequest,
  LoginRequest,
  Norm,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateUserPasswordRequest,
  UpdateUserRequest,
  User,
  VerifyEmailRequest,
} from '../../types/user.ts';

export interface UserState {
  user?: User;
  loginError: boolean;
  registerErrorMessage: string;
  searchQuery: string;
  resetPasswordRequestSent: boolean;
  changePasswordSuccess: boolean;
  profilePictureUrl?: string;
  norms: Norm[];
  verificationSuccess: boolean;
  verificationError: string;
  unsubscribeSuccess: boolean;
  unsubscribeError: string;
  isLoadingUser: boolean;
  isImpersonating: boolean;
  impersonationError: string;
  isSendingInvitation: boolean;
  invitationSuccess: boolean;
  invitationError: string;
  isSendingCredits: boolean;
  creditsSuccess: boolean;
  creditsError: string;
  isCopyingProject: boolean;
  copiedProject?: CopiedProject;
  copyProjectError: string;
  allUsers: User[];
  isLoadingUsers: boolean;
  usersError: string;
}

const initialState: UserState = {
  user: undefined,
  loginError: false,
  registerErrorMessage: '',
  searchQuery: '',
  resetPasswordRequestSent: false,
  changePasswordSuccess: false,
  profilePictureUrl: undefined,
  norms: [],
  verificationSuccess: false,
  verificationError: '',
  unsubscribeSuccess: false,
  unsubscribeError: '',
  isLoadingUser: false,
  isImpersonating: false,
  impersonationError: '',
  isSendingInvitation: false,
  invitationSuccess: false,
  invitationError: '',
  isSendingCredits: false,
  creditsSuccess: false,
  creditsError: '',
  isCopyingProject: false,
  copiedProject: undefined,
  copyProjectError: '',
  allUsers: [],
  isLoadingUsers: false,
  usersError: '',
};

export const userReducer = createSlice({
  name: 'user',
  initialState,
  reducers: {
    login: (_state: UserState, _action: PayloadAction<LoginRequest>) => noop(),
    setLoginError: (state: UserState, action: PayloadAction<boolean>) => {
      state.loginError = action.payload;
    },
    register: (_state: UserState, _action: PayloadAction<RegisterRequest>) => noop(),
    setRegisterErrorMessage: (state: UserState, action: PayloadAction<string>) => {
      state.registerErrorMessage = action.payload;
    },
    logout: (_state: UserState) => noop(),
    loadUser: (state: UserState) => {
      state.isLoadingUser = true;
    },
    /** Reload the user (e.g. the credit balance) in the background, without the app-wide loading state. */
    refreshUser: (_state: UserState) => noop(),
    setUser: (state: UserState, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isLoadingUser = false;
    },
    setUserLoadingComplete: (state: UserState) => {
      state.isLoadingUser = false;
    },
    setSearchQuery: (state: UserState, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPassword: (state: UserState, _action: PayloadAction<ResetPasswordRequest>) => {
      state.resetPasswordRequestSent = true;
    },
    clearResetPasswordState: (state: UserState) => {
      state.resetPasswordRequestSent = false;
    },
    changePassword: (state: UserState, _action: PayloadAction<ChangePasswordRequest>) => {
      state.changePasswordSuccess = true;
    },
    loadProfilePicture: (_state: UserState, _action: PayloadAction<number>) => noop(),
    setProfilePicture: (state: UserState, action: PayloadAction<string>) => {
      state.profilePictureUrl = action.payload;
    },
    updateUser: (_state: UserState, _action: PayloadAction<UpdateUserRequest>) => noop(),
    updateHideOnboarding: (_state: UserState) => noop(),
    updateUserPassword: (_state: UserState, _action: PayloadAction<UpdateUserPasswordRequest>) => noop(),
    loadNorms: (_state: UserState) => noop(),
    setNorms: (state: UserState, action: PayloadAction<Norm[]>) => {
      state.norms = action.payload;
    },
    verifyEmail: (_state: UserState, _action: PayloadAction<VerifyEmailRequest>) => noop(),
    setVerificationSuccess: (state: UserState, action: PayloadAction<boolean>) => {
      state.verificationSuccess = action.payload;
      state.verificationError = '';
    },
    setVerificationError: (state: UserState, action: PayloadAction<string>) => {
      state.verificationError = action.payload;
      state.verificationSuccess = false;
    },
    unsubscribeNewsletter: (_state: UserState, _action: PayloadAction<{ token: string; unsubscribeReason?: string }>) =>
      noop(),
    setUnsubscribeSuccess: (state: UserState, action: PayloadAction<boolean>) => {
      state.unsubscribeSuccess = action.payload;
      state.unsubscribeError = '';
    },
    setUnsubscribeError: (state: UserState, action: PayloadAction<string>) => {
      state.unsubscribeError = action.payload;
      state.unsubscribeSuccess = false;
    },
    impersonate: (state: UserState, _action: PayloadAction<ImpersonateRequest>) => {
      state.isImpersonating = true;
      state.impersonationError = '';
    },
    setImpersonationSuccess: (state: UserState) => {
      state.isImpersonating = false;
      state.impersonationError = '';
    },
    setImpersonationError: (state: UserState, action: PayloadAction<string>) => {
      state.isImpersonating = false;
      state.impersonationError = action.payload;
    },
    sendInvitation: (state: UserState, _action: PayloadAction<InvitationRequest>) => {
      state.isSendingInvitation = true;
      state.invitationSuccess = false;
      state.invitationError = '';
    },
    setInvitationSuccess: (state: UserState) => {
      state.isSendingInvitation = false;
      state.invitationSuccess = true;
      state.invitationError = '';
    },
    setInvitationError: (state: UserState, action: PayloadAction<string>) => {
      state.isSendingInvitation = false;
      state.invitationSuccess = false;
      state.invitationError = action.payload;
    },
    sendCredits: (state: UserState, _action: PayloadAction<CreditsRequest>) => {
      state.isSendingCredits = true;
      state.creditsSuccess = false;
      state.creditsError = '';
    },
    setCreditsSuccess: (state: UserState) => {
      state.isSendingCredits = false;
      state.creditsSuccess = true;
      state.creditsError = '';
    },
    setCreditsError: (state: UserState, action: PayloadAction<string>) => {
      state.isSendingCredits = false;
      state.creditsSuccess = false;
      state.creditsError = action.payload;
    },
    copyProject: (state: UserState, _action: PayloadAction<CopyProjectRequest>) => {
      state.isCopyingProject = true;
      state.copiedProject = undefined;
      state.copyProjectError = '';
    },
    setCopyProjectSuccess: (state: UserState, action: PayloadAction<CopiedProject>) => {
      state.isCopyingProject = false;
      state.copiedProject = action.payload;
      state.copyProjectError = '';
    },
    setCopyProjectError: (state: UserState, action: PayloadAction<string>) => {
      state.isCopyingProject = false;
      state.copyProjectError = action.payload;
    },
    clearCopiedProject: (state: UserState) => {
      state.isCopyingProject = false;
      state.copiedProject = undefined;
      state.copyProjectError = '';
    },
    loadUsers: (state: UserState) => {
      state.isLoadingUsers = true;
      state.usersError = '';
    },
    setUsers: (state: UserState, action: PayloadAction<User[]>) => {
      state.allUsers = action.payload;
      state.isLoadingUsers = false;
      state.usersError = '';
    },
    setUsersError: (state: UserState, action: PayloadAction<string>) => {
      state.isLoadingUsers = false;
      state.usersError = action.payload;
    },
  },
});
